/**
 * Shared popup print pipeline (same idea as BeamFormulae openPrintReport).
 */

import { escapeHtml } from './eurocodeNotation';

export { escapeHtml };

export function buildSharedPrintStyles() {
  return `
  @page { margin: 12mm; size: A4; }
  body { font-family: system-ui, Segoe UI, Roboto, Arial, sans-serif; font-size: 10pt; color: #111; line-height: 1.35; }
  h1 { font-size: 16pt; margin: 0 0 8px; page-break-after: avoid; }
  h2 { font-size: 12pt; margin: 14px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; page-break-after: avoid; }
  h3 { font-size: 10.5pt; margin: 10px 0 6px; page-break-after: avoid; }
  .muted { color: #666; font-size: 9pt; }
  .section {
    margin-bottom: 14px;
    page-break-inside: avoid;
  }
  .section.allow-break {
    page-break-inside: auto;
  }
  .section.break-before {
    page-break-before: always;
  }
  table.report {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0 12px;
    font-size: 9pt;
  }
  table.report th, table.report td {
    border: 1px solid #ccc;
    padding: 5px 7px;
    vertical-align: top;
  }
  table.report thead { display: table-header-group; }
  table.report th { background: #eee; text-align: left; font-weight: 600; }
  table.report td.num, table.report th.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.report.allow-row-split tr { page-break-inside: auto; }
  table.report tr { page-break-inside: avoid; page-break-after: auto; }
  .kv-table td:first-child { width: 32%; font-weight: 600; background: #f7f7f7; }
  .chart-wrap, .svg-wrap {
    page-break-inside: avoid;
    margin: 10px 0;
    text-align: center;
  }
  .chart-wrap img, .svg-wrap svg {
    max-width: 100%;
    height: auto;
    border: 1px solid #ddd;
  }
  .svg-wrap { border: 1px solid #ddd; padding: 8px; }
  sub { font-size: 0.85em; }
  @media print {
    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
  }
`;
}

/**
 * @param {string} title — already safe or use escapeHtml caller-side for dynamic titles
 * @param {string} innerHtml — trusted HTML fragment
 * @param {{ breakBefore?: boolean, allowBreak?: boolean, className?: string }} [opts]
 */
export function printSectionHtml(title, innerHtml, opts = {}) {
  const { breakBefore = false, allowBreak = false, className = '' } = opts;
  const classes = [
    'section',
    breakBefore ? 'break-before' : '',
    allowBreak ? 'allow-break' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const h = title ? `<h2>${escapeHtml(title)}</h2>` : '';
  return `<section class="${classes}">${h}${innerHtml}</section>`;
}

/**
 * @param {{ title: string, subtitle?: string, bodyHtml: string }} opts
 */
export function buildPrintDocumentHtml({ title, subtitle = '', bodyHtml }) {
  const styles = buildSharedPrintStyles();
  const sub = subtitle
    ? `<p class="muted">${escapeHtml(subtitle)}</p>`
    : '';
  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${escapeHtml(title)}</title><style>${styles}</style></head><body><header><h1>${escapeHtml(title)}</h1>${sub}</header>${bodyHtml}</body></html>`;
}

/**
 * Open print dialog for a full HTML document string.
 * @param {string} html
 */
export function openPrintHtmlDocument(html) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) {
    URL.revokeObjectURL(url);
    window.alert('Pop-up blocked. Allow pop-ups for this site to open the print report.');
    return;
  }
  let printCalled = false;
  const runPrint = () => {
    if (printCalled) return;
    printCalled = true;
    try {
      w.focus();
      w.print();
    } catch (e) {
      console.error(e);
    }
  };
  if (w.document.readyState === 'complete') {
    setTimeout(runPrint, 250);
  } else {
    w.addEventListener('load', () => setTimeout(runPrint, 250), { once: true });
    setTimeout(runPrint, 2000);
  }
  w.addEventListener(
    'beforeunload',
    () => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        /* ignore */
      }
    },
    { once: true },
  );
}
